import { useSignIn } from "@clerk/nextjs";
import { isClerkAPIResponseError } from "@clerk/nextjs/errors";
import type { OAuthStrategy } from "@clerk/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, InputField, Panel } from "@procision-software/ui";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import googleLogo from "~/assets/images/google.svg?resource";
import microsoftLogo from "~/assets/images/microsoft.svg?resource";
import logo from "~/assets/images/procision_logo_title.svg?resource";
import schedule from "~/assets/images/schedule.svg?resource";
import HeadlessLayout from "~/components/layouts/headless";
import { useRouter } from "~/hooks/useRouter";
import { wrapWithLayout } from "~/utils/wrap-with-layout";

const FormSchema = z.object({
  email: z.string().email().min(1, { message: "Required" }),
  password: z.string().min(1, { message: "Required" }),
});

type FormData = z.infer<typeof FormSchema>;

const SignInPage = () => {
  const router = useRouter();
  const { redirectUrl } = router.query as { redirectUrl?: string };
  const { isLoaded, signIn, setActive } = useSignIn();
  const [loginError, setLoginError] = useState<string | null>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const afterSignInRedirectUrl = redirectUrl ? `/?redirectUrl=${redirectUrl}` : "/";

  const signInWithEmail = useCallback(
    async ({ email, password }: FormData) => {
      if (!isLoaded) return;
      setLoginError(null);
      try {
        const signInAttempt = await signIn.create({
          identifier: email,
          password,
        });

        if (signInAttempt.status === "complete") {
          await setActive({ session: signInAttempt.createdSessionId });
          await router.replace(redirectUrl ?? "/");
        } else {
          setLoginError("Unable to sign in at this time.");
          console.error(JSON.stringify(signInAttempt, null, 2));
        }
      } catch (err) {
        // See https://clerk.com/docs/custom-flows/error-handling for more info on error handling
        if (isClerkAPIResponseError(err)) {
          if (err.errors.some((e) => e.code === "identifier_already_signed_in")) {
            await router.replace(redirectUrl ?? "/");
          } else {
            setLoginError(err.errors[0]?.message ?? "Unable to sign in at this time.");
          }
        } else {
          console.error(err);
          setLoginError("Unable to sign in at this time.");
        }
      }
    },
    [isLoaded, signIn, setActive, router, redirectUrl]
  );

  const signInWithOAuth = useCallback(
    async (strategy: OAuthStrategy) => {
      if (!isLoaded) return;
      setLoginError(null);
      try {
        await signIn.authenticateWithRedirect({
          strategy,
          redirectUrl: "/sso-callback",
          redirectUrlComplete: afterSignInRedirectUrl,
        });
      } catch (err) {
        if (isClerkAPIResponseError(err)) {
          if (err.errors.some((e) => e.code === "identifier_already_signed_in")) {
            await router.replace(redirectUrl ?? "/");
          } else {
            setLoginError(err.errors[0]?.message ?? "Unable to sign in at this time.");
          }
        } else {
          console.error(err);
          setLoginError("Unable to sign in at this time.");
        }
      }
    },
    [isLoaded, signIn, afterSignInRedirectUrl, router, redirectUrl]
  );

  const emailInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    emailInputRef.current?.focus();
    emailInputRef.current?.select();
  }, [emailInputRef]);

  return (
    <div className="flex grow items-center justify-center">
      <div className="flex flex-col gap-10">
        <Image src={logo} width={217} height={65} alt="Procision Logo" className="ml-2" />
        <div className="flex flex-row items-center gap-20">
          <div className="">
            <Image src={schedule} width={723} height={592} alt="Procision Logo" />
          </div>
          <div className="flex w-96 flex-col gap-6">
            <h1 className="text-2xl text-primary">Sign In</h1>
            <form className="flex flex-col gap-2" onSubmit={handleSubmit(signInWithEmail)}>
              <InputField
                ref={emailInputRef}
                control={form.control}
                name="email"
                label="Email Address"
                className="w-full"
                type="email"
              />
              <InputField
                control={form.control}
                name="password"
                label="Password"
                className="w-full"
                type="password"
              />
              {loginError && <Panel variant="error">{loginError}</Panel>}
              <div className="text-center">
                <Link
                  className="cursor-pointer text-xs text-secondary underline-offset-2
                    hover:text-brand-hover hover:underline"
                  href="/forgot-password"
                >
                  Forgot password? Click here.
                </Link>
              </div>
              <Button
                type="submit"
                variant="primary"
                outline={false}
                disabled={!isLoaded || isSubmitting}
                loading={isSubmitting}
                className="w-full"
              >
                Continue
              </Button>
            </form>
            <div className="flex flex-row items-center justify-center gap-6 text-gray-300">
              <hr className="h-px grow border-0 bg-gray-300" />
              <div>or</div>
              <hr className="h-px grow border-0 bg-gray-300" />
            </div>
            <div className="flex flex-col gap-4">
              <Button
                variant="primary"
                outline={true}
                type="button"
                onClick={async () => await signInWithOAuth("oauth_google")}
                disabled={!isLoaded}
              >
                <Image src={googleLogo} width={16} height={16} alt="Google" /> Sign in with Google
              </Button>
              <Button
                variant="primary"
                outline={true}
                type="button"
                onClick={async () => await signInWithOAuth("oauth_microsoft")}
                disabled={!isLoaded}
              >
                <Image src={microsoftLogo} width={16} height={16} alt="Microsoft" /> Sign in with
                Microsoft
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default wrapWithLayout(HeadlessLayout, SignInPage);
